import React, { useCallback, useMemo, useRef, useState } from 'react'
import { intl } from 'lang/intl'

import {
  TtButton,
  TtButtonGroup,
  TtDropdown,
  TtIcon,
  TtTextField,
} from '@23/react'

import { useSelectedChatKey } from '../../blocs/chatVisibility'
import { usePostChatMessageMutation } from '../../blocs/useChatQuery'
import { ChatReactionsSelector } from '../ChatReactions/ChatReactionsSelector'
import { selectReplyComment, useActiveReplyComment } from '../Reply/Reply'
import { getRole } from '../../../global/blocs/roomInfo'
import { useAudienceName } from '../../blocs/useAudienceName'
import { useAskAudienceName } from '../AskAudienceName/AskAudienceNameModal'

import './ChatForm.scss'

const ChatForm = () => {
  const selectedChat = useSelectedChatKey()
  const activeReply = useActiveReplyComment()
  const { mutate: postMessage } = usePostChatMessageMutation(selectedChat)
  const [message, setMessage] = useState('')
  const ref = useRef<HTMLTtDropdownElement>(null)

  const audienceName = useAudienceName()
  const nameRequired = useMemo(
    () => getRole() === 'audience' && !audienceName,
    [audienceName]
  )

  const openAskAudienceNameModal = useAskAudienceName()

  const submit = useCallback(async () => {
    if (nameRequired) {
      const finished = await openAskAudienceNameModal()
      if (!finished) return
    }

    postMessage({
      message,
      replyId: activeReply ?? undefined,
    })
    setMessage('')
    selectReplyComment(null)
  }, [
    nameRequired,
    postMessage,
    message,
    activeReply,
    openAskAudienceNameModal,
  ])

  const onReactionClicked = useCallback(
    async (reaction: string) => {
      if (nameRequired) {
        const finished = await openAskAudienceNameModal()
        if (!finished) return
      }

      ref.current?.hide()
      postMessage({
        message: reaction,
      })
    },
    [nameRequired, postMessage, openAskAudienceNameModal]
  )

  const placeholders = {
    chat: intl.formatMessage({
      defaultMessage: 'Write your message here...',
    }),
    'speaker-chat': intl.formatMessage({
      defaultMessage: 'Chat with the other speakers',
    }),
  }

  return (
    <div className="webinar-chat-form">
      <div className="chat-form">
        <TtDropdown
          ref={ref}
          className="reactions-picker"
          placement="top-start"
          distance={10}
          hoist
        >
          <TtButton
            slot="trigger"
            color="secondary"
            variant="light"
            size="large"
            className="reactions-picker-trigger"
            circle
          >
            <TtIcon library="one/webinars" name="add-reaction" />
          </TtButton>

          <ChatReactionsSelector onReactionSelected={onReactionClicked} />
        </TtDropdown>
        <TtButtonGroup className="chat-form-group">
          <TtTextField
            className="chat-form-text-field"
            name="message"
            label={intl.formatMessage({ defaultMessage: 'Add a new comment' })}
            hideLabel
            size="large"
            placeholder={placeholders[selectedChat]}
            value={message}
            onTt-input={(e) => {
              setMessage(e.detail.value)
            }}
            onKeyDown={async (e) => {
              if (e.key === 'Enter' && !!message) {
                e.preventDefault()
                submit()
              }
            }}
          />
          <TtButton
            className="chat-form-submit-btn"
            variant="light"
            color="secondary"
            circle
            size="medium"
            disabled={!message}
            onClick={async () => {
              submit()
            }}
          >
            <TtIcon library="one/webinars" name="send" />
          </TtButton>
        </TtButtonGroup>
      </div>
    </div>
  )
}

export { ChatForm }
